import { useEffect } from "react";
import { useDispatch, useSelector } from "react-redux";
import { consumeEventStream, onNetworkResume } from "../services/eventStream.js";
import {
  normalizeRecentTasks,
  isRecentTasksFrame,
} from "../services/recentTasks.js";
import {
  recentTasksSet,
  recentTasksReset,
} from "../store/slices/recentTasksSlice.js";
import { selectIsAuthenticated } from "../store/slices/authSlice.js";

// Re-open delays. A stream that stayed open and then dropped is a genuine
// disconnect — reconnect promptly. A stream that closes the moment it
// answers means the endpoint ISN'T holding the connection open (it just
// writes a snapshot and ends), so re-opening on a short timer would be
// polling by another name; back off instead, doubling up to the cap.
const RECONNECT_MIN_MS = 4000;
const RECONNECT_MAX_MS = 30000;
// A connection alive at least this long counts as healthy.
const HEALTHY_CONNECTION_MS = 15000;

/**
 * Subscribes to `GET /task/events/recent` — the Server-Sent Events feed
 * that keeps the Sidebar's "My Tasks" list fresh — for the lifetime of a
 * logged-in session, and pushes each frame into the RTK Query cache the
 * Sidebar already reads (`getRecentTasks`).
 *
 * This is the ONLY source of the recent-tasks list — the old `/task/`
 * GET + interval polling has been removed entirely. Call it once at a
 * login-scoped mount point (the authenticated app shell): it opens on
 * login, reconnects if the stream drops, resets the list on logout, and
 * tears down on unmount.
 *
 * Each frame is a full snapshot written to `recentTasksSlice`, which the
 * Sidebar reads via `selectRecentTasks`.
 */
export default function useRecentTasksStream() {
  const dispatch = useDispatch();
  const isAuthenticated = useSelector(selectIsAuthenticated);

  useEffect(() => {
    if (!isAuthenticated) return undefined;

    let stopped = false;
    let controller = null;
    let retryTimer = null;
    let retryDelay = RECONNECT_MIN_MS;
    // True from the instant a connect attempt starts until its stream
    // closes — so ``reconnectNow`` only fires when the stream is actually
    // DOWN and never tears a healthy connection down.
    let active = false;

    const connect = () => {
      if (stopped) return;
      active = true;
      controller = new AbortController();
      const openedAt = Date.now();
      consumeEventStream("/task/events/recent?limit=70", {
        signal: controller.signal,
        onFrame: (frame) => {
          if (stopped) return;
          // A persistent stream also carries heartbeats / other event
          // types. Only a frame that actually holds a list is a snapshot;
          // applying anything else would blank the sidebar.
          if (!isRecentTasksFrame(frame)) return;
          dispatch(recentTasksSet(normalizeRecentTasks(frame)));
        },
      })
        .catch(() => {
          // An abort (logout / unmount) is the expected teardown path; a
          // real drop falls through to the reconnect below. Either way
          // there's nothing to surface — the last snapshot stays on
          // screen until the stream is back.
        })
        .finally(() => {
          active = false;
          if (stopped) return;
          // Reset the backoff only for a connection that actually stayed
          // open; otherwise grow it, so an endpoint that closes instantly
          // isn't re-opened every few seconds.
          // Schedule THIS retry at the current delay, then grow it for
          // the next one — so the first re-open is always prompt and only
          // a repeatedly-instant-closing endpoint gets backed off. A
          // connection that actually stayed open resets the ladder.
          const delay = retryDelay;
          retryDelay =
            Date.now() - openedAt >= HEALTHY_CONNECTION_MS
              ? RECONNECT_MIN_MS
              : Math.min(retryDelay * 2, RECONNECT_MAX_MS);
          retryTimer = setTimeout(connect, delay);
        });
    };

    // Network back / tab visible again → skip the reconnect backoff and
    // re-open immediately (a net::ERR_NETWORK_CHANGED on a WiFi/VPN/sleep
    // transition cancels the open stream; without this it'd stay dead for
    // up to RECONNECT_* seconds). Only when the stream is actually down.
    const reconnectNow = () => {
      if (stopped || active) return;
      if (retryTimer) clearTimeout(retryTimer);
      retryDelay = RECONNECT_MIN_MS;
      connect();
    };
    const stopResume = onNetworkResume(reconnectNow);

    connect();

    return () => {
      stopped = true;
      if (controller) controller.abort();
      if (retryTimer) clearTimeout(retryTimer);
      stopResume();
      // Effect teardown only happens on logout / unmount (deps are auth +
      // the stable dispatch), so clearing here can't wipe the list on a
      // reconnect — those loop inside the same effect run.
      dispatch(recentTasksReset());
    };
  }, [isAuthenticated, dispatch]);
}
